import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Loader from './Loader';
import Sidebar from './Sidebar';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import { firestore } from '../firebase.config';
import {
    collection,
    getDocs,
    deleteDoc,
    doc,
    setDoc,
} from 'firebase/firestore';
import { Pencil, Trash2 } from 'lucide-react';
import moment from 'moment';

const Videogallery = () => {
    // State for new video entry
    const [heading, setHeading] = useState('');
    const [description, setDescription] = useState('');
    const [uploadedVideoUrls, setUploadedVideoUrls] = useState([]); // Changed to uploadedVideoUrls
    const [uploadProgress, setUploadProgress] = useState(0);

    // State for general UI feedback
    const [isLoading, setIsLoading] = useState(false);
    const [field, setField] = useState(false);
    const [alertStatus, setAlertStatus] = useState('danger');
    const [msg, setMsg] = useState(null);

    // State for sidebar
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // State for video list display
    const [videoList, setVideoList] = useState([]);

    // States for edit modal
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editVideoData, setEditVideoData] = useState(null);
    const [editUploadedVideoUrls, setEditUploadedVideoUrls] = useState([]); // Changed to editUploadedVideoUrls
    const [editUploadProgress, setEditUploadProgress] = useState(0);

    const cloudName = "dabhatz7m"; // Your Cloudinary cloud name
    const uploadPreset = "ImageUpload"; // Your Cloudinary upload preset (consider changing its name if it's now for videos)

    // Toggle sidebar function
    const toggleSidebar = (state) => {
        setIsSidebarOpen(state);
    };

    // Reusable Video Upload Handler for both add and edit
    const handleVideoUpload = (e, isEdit = false) => { // Renamed to handleVideoUpload
        e.preventDefault();
        const file = e.target.files[0];

        if (!file) {
            toast.error("Please select a video to upload!");
            return;
        }

        setIsLoading(true); // Indicate loading for video upload
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", uploadPreset);
        formData.append('folder', 'Videos'); // Store in "Videos" folder in Cloudinary (recommended)

        const xhr = new XMLHttpRequest();
        xhr.open("POST", `https://api.cloudinary.com/v1_1/${cloudName}/video/upload`, true); // Changed to /video/upload

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                const percentComplete = (event.loaded / event.total) * 100;
                if (isEdit) {
                    setEditUploadProgress(percentComplete);
                } else {
                    setUploadProgress(percentComplete);
                }
            }
        };

        xhr.onload = () => {
            setIsLoading(false); // Stop loading after upload attempt
            if (xhr.status === 200) {
                const data = JSON.parse(xhr.responseText);
                if (!data.secure_url) {
                    console.error("Secure URL is missing from Cloudinary response");
                    toast.error("Video upload failed: secure URL missing.");
                    return;
                }
                const videoUrl = data.secure_url; // Renamed to videoUrl

                if (isEdit) {
                    setEditUploadedVideoUrls((prevUrls) => [...prevUrls, videoUrl]); // Updated state
                    setEditUploadProgress(0);
                    toast.success("Video uploaded successfully for editing!");
                } else {
                    setUploadedVideoUrls((prevUrls) => [...prevUrls, videoUrl]); // Updated state
                    setUploadProgress(0);
                    toast.success("Video uploaded successfully!");
                }
            } else {
                console.error("Failed to upload video to Cloudinary", xhr.statusText);
                toast.error("Failed to upload video to Cloudinary.");
                if (isEdit) {
                    setEditUploadProgress(0);
                } else {
                    setUploadProgress(0);
                }
            }
        };

        xhr.onerror = () => {
            setIsLoading(false); // Stop loading on error
            console.error("Network error during video upload:", xhr.statusText);
            toast.error("Network error during video upload.");
            if (isEdit) {
                setEditUploadProgress(0);
            } else {
                setUploadProgress(0);
            }
        };

        xhr.send(formData);
    };

    // Reusable Video Deletion Handler for both add and edit
    const handleDeleteVideoFromSelection = (index, isEdit = false) => { // Renamed for clarity
        if (isEdit) {
            const updatedVideos = [...editUploadedVideoUrls];
            updatedVideos.splice(index, 1);
            setEditUploadedVideoUrls(updatedVideos);
            toast.info("Video removed from selection.");
        } else {
            const updatedVideos = [...uploadedVideoUrls];
            updatedVideos.splice(index, 1);
            setUploadedVideoUrls(updatedVideos);
            toast.info("Video removed from selection.");
        }
    };

    // Save new video entry
    const saveDetails = async () => {
        setIsLoading(true);
        // Validate inputs
        if (!heading || !description) {
            setField(true);
            setMsg("Heading and Description can't be empty");
            setAlertStatus("danger");
            toast.error("Heading and Description can't be empty");
            setTimeout(() => {
                setField(false);
                setIsLoading(false);
            }, 4000);
            return;
        }

        if (uploadedVideoUrls.length === 0) { // Changed to uploadedVideoUrls
            setField(true);
            setMsg("Please upload at least one video.");
            setAlertStatus("danger");
            toast.error("Please upload at least one video.");
            setIsLoading(false);
            setTimeout(() => {
                setField(false);
            }, 4000);
            return;
        }

        try {
            const id = `${Date.now()}`; // Unique ID for the document

            const data = {
                id: id,
                heading,
                description,
                videoUrls: [...uploadedVideoUrls], // Changed to videoUrls
                createdAt: moment().toISOString(), // Add a timestamp for creation
            };

            // Directly save to Firestore 'Video' collection
            await setDoc(doc(firestore, 'Video', id), data);

            setIsLoading(false);
            setField(true);
            setMsg("Video details saved successfully!");
            setAlertStatus("success");
            toast.success("Video details saved successfully!");
            clearData();
            fetchVideos(); // Refresh the list after saving

            setTimeout(() => {
                setField(false);
            }, 4000);
        } catch (error) {
            console.error("Error saving video details:", error);
            toast.error("Failed to save video details.");
            setIsLoading(false);
        }
    };

    // Clear form data after saving
    const clearData = () => {
        setHeading('');
        setDescription('');
        setUploadedVideoUrls([]); // Updated state
    };

    // Fetch all video entries
    const fetchVideos = async () => {
        try {
            const querySnapshot = await getDocs(collection(firestore, 'Video'));
            const videos = [];
            querySnapshot.forEach((doc) => {
                videos.push({ ...doc.data(), docId: doc.id });
            });
            // Sort videos by creation date, newest first
            setVideoList(videos.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        } catch (error) {
            console.error("Error fetching videos:", error);
            toast.error("Failed to fetch videos.");
        }
    };

    // Delete a video entry
    const handleDeleteVideo = async (docId) => {
        if (window.confirm("Are you sure you want to delete this video entry?")) {
            try {
                await deleteDoc(doc(firestore, 'Video', docId));
                toast.success("Video entry deleted successfully!");
                fetchVideos(); // Refresh the list after deletion
            } catch (error) {
                console.error("Error deleting video entry:", error);
                toast.error("Failed to delete video entry.");
            }
        }
    };

    // Open edit modal and populate with existing data
    const openEditModal = (videoItem) => {
        setEditVideoData({ ...videoItem });
        setEditUploadedVideoUrls([...videoItem.videoUrls]); // Updated state
        setEditModalOpen(true);
    };

    // Handle update of video entry
    const handleUpdateVideo = async () => {
        setIsLoading(true);
        if (!editVideoData?.heading || !editVideoData?.description) {
            toast.error("Please fill in all required fields for the video.");
            setIsLoading(false);
            return;
        }

        if (editUploadedVideoUrls.length === 0) { // Changed to editUploadedVideoUrls
            toast.error("Please upload at least one video.");
            setIsLoading(false);
            return;
        }

        try {
            const ref = doc(firestore, 'Video', editVideoData.docId);
            await setDoc(ref, {
                id: editVideoData.id,
                heading: editVideoData.heading,
                description: editVideoData.description,
                videoUrls: editUploadedVideoUrls, // Updated field name
                createdAt: editVideoData.createdAt,
                updatedAt: moment().toISOString(),
            }, { merge: true });

            toast.success("Video entry updated successfully!");
            setEditModalOpen(false);
            setIsLoading(false);
            fetchVideos(); // Refresh the list after update
        } catch (err) {
            console.error("Error updating video entry:", err);
            toast.error("Failed to update video entry.");
            setIsLoading(false);
        }
    };

    // Fetch videos on component mount
    useEffect(() => {
        fetchVideos();
    }, []);

    return (
        <div className='lg:flex h-screen bg-white'>
            <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
            <div className={`flex-1 p-1 lg:p-12 overflow-y-auto transition-all duration-300 lg:mt-2 mt-16 ${isSidebarOpen ? 'ml-56 p-0' : 'lg:ml-20 sm:ml-2 '}`}>
                <div className={`w-full max-w-4xl mx-auto bg-white shadow-xl rounded-2xl lg:p-8 ${isSidebarOpen ? ' p-1' : 'lg:p-8 p-8'}`}>
                    <h1 className={`lg:text-2xl font-bold text-[#fcaf17] mb-6 ${isSidebarOpen ? 'text-[13px]' : ''}`}>Add Video Entry</h1>

                    {field && (
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className={`p-3 rounded-lg text-center text-lg font-medium ${alertStatus === "danger" ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"}`}
                        >
                            {msg}
                        </motion.p>
                    )}

                    <div className='lg:space-y-3 space-y-1'>
                        <div>
                            <label className={`block text-gray-700 text-sm font-medium mb-2 ${isSidebarOpen ? 'text-xs' : ''}`} htmlFor='heading'>Heading</label>
                            <input
                                id='heading'
                                type='text'
                                value={heading}
                                autoComplete='off'
                                placeholder='Enter Heading'
                                onChange={(e) => setHeading(e.target.value)}
                                className={`w-full px-4 py-2 border border-gray-300 lg:text-md text-sm rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none ${isSidebarOpen ? 'text-xs' : ''}`}
                            />
                        </div>

                        <div>
                            <label
                                className={`block text-gray-700 text-sm font-medium mb-2 ${isSidebarOpen ? 'text-xs' : ''
                                    }`}
                                htmlFor="description"
                            >
                                Description
                            </label>
                            <textarea
                                id="description"
                                value={description}
                                autoComplete="off"
                                placeholder="Enter Description"
                                onChange={(e) => setDescription(e.target.value)}
                                className={`w-full px-4 py-2 border border-gray-300 lg:text-md text-sm rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none ${isSidebarOpen ? 'text-xs' : ''
                                    }`}
                            />
                        </div>

                        <div className="mb-4">
                            <label
                                htmlFor="videoInput" // Changed ID
                                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
                            >
                                <svg
                                    className="mr-2 h-5 w-5 text-[#fcaf17]"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                <span>Upload Video(s)</span> {/* Changed text */}
                            </label>
                            <input
                                type="file"
                                id="videoInput" // Changed ID
                                accept="video/*" // Changed accept attribute to video/*
                                onChange={(e) => handleVideoUpload(e, false)} // Changed handler
                                multiple
                                className="hidden"
                            />
                        </div>

                        {uploadProgress > 0 && (
                            <div className="mt-2">
                                <div className="bg-gray-200 rounded-full h-2.5 mb-4 dark:bg-gray-700">
                                    <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                                </div>
                                <p className="text-sm text-gray-600 text-center">{Math.round(uploadProgress)}%</p>
                            </div>
                        )}
                        <div id="videosContainer" className="flex flex-wrap gap-4 mt-2"> {/* Changed ID */}
                            {uploadedVideoUrls.map((url, index) => ( // Iterating over video URLs
                                <div
                                    key={index}
                                    className="relative w-[200px] h-[130px] border rounded-md overflow-hidden"
                                >
                                    <video
                                        src={url}
                                        controls // Add controls to play the video
                                        alt={`Uploaded Video ${index}`}
                                        className="w-full h-full object-cover"
                                        preload="metadata" // Suggestion for faster loading of video metadata
                                    />
                                    <button
                                        onClick={() => handleDeleteVideoFromSelection(index, false)} // Changed handler
                                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                        title="Remove video" // Changed title
                                    >
                                        ✕
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-start">
                            <button
                                className="bg-blue-600 text-white lg:text-sm text-xs py-2 px-4 rounded-lg hover:bg-blue-700 transition duration-300"
                                onClick={saveDetails}
                            >
                                {isLoading ? <Loader /> : 'Save Video Entry'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* --- Table Section - Conditional Rendering --- */}
                {videoList.length > 0 ? (
                    <div className="mt-12 p-8 transform transition-all duration-300 hover:shadow-yellow-300/40">
                        <h2 className="text-3xl font-extrabold mb-8 text-[#fcaf17] tracking-wide flex items-center gap-3 animate-fade-in-down">
                            <span>✨</span> Video Collection
                        </h2>

                        <div className="overflow-hidden rounded-2xl border border-yellow-200 shadow-lg transform transition-all duration-300 hover:shadow-yellow-200/20">
                            <table className="min-w-full text-sm text-left text-gray-700">
                                <thead className="bg-gradient-to-r from-yellow-100 to-yellow-200/70 backdrop-blur-sm border-b border-yellow-300 text-[14px] uppercase font-bold tracking-wider text-orange-800 animate-slide-in-top">
                                    <tr>
                                        <th className="px-6 py-4">Sl. No.</th>
                                        <th className="px-6 py-4">Heading</th>
                                        <th className="px-6 py-4">Description</th>
                                        <th className="px-6 py-4">Video</th> {/* Changed from Thumbnail */}
                                        <th className="px-6 py-4 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {videoList.map((item, index) => (
                                        <tr
                                            key={item.docId}
                                            className="group bg-white/70 transition-all duration-300 hover:bg-orange-50/70 border-b border-orange-100 last:border-b-0 animate-fade-in"
                                        >
                                            <td className="px-6 py-4 font-semibold text-gray-800 text-base">
                                                {index + 1}
                                            </td>

                                            <td className="px-6 py-4 text-gray-900 font-medium">{item.heading}</td>
                                            <td className="px-6 py-4 text-gray-600 text-sm leading-relaxed">
                                                {item.description}
                                            </td>
                                            <td className="px-6 py-4">
                                                {item.videoUrls && item.videoUrls.length > 0 && (
                                                    <video
                                                        src={item.videoUrls[0]} // Displaying the first video
                                                        controls
                                                        className="w-28 h-20 object-cover rounded-xl border-2 border-gray-200 shadow-md group-hover:scale-105 group-hover:border-orange-400 transition-all duration-300 ease-in-out cursor-pointer"
                                                        preload="metadata"
                                                    />
                                                )}
                                            </td>
                                            <td className="px-6 py-4 text-center flex justify-center gap-2">
                                                <button
                                                    onClick={() => openEditModal(item)}
                                                    className="inline-flex items-center gap-2 text-blue-600 hover:text-white bg-blue-100 hover:bg-blue-500 px-4 py-2 rounded-full text-sm font-semibold shadow-md transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2"
                                                    title="Edit Video Entry"
                                                >
                                                    <Pencil size={16} />
                                                    Edit
                                                </button>

                                                <button
                                                    onClick={() => handleDeleteVideo(item.docId)}
                                                    className="inline-flex items-center gap-2 text-red-600 hover:text-white bg-red-100 hover:bg-red-500 px-4 py-2 rounded-full text-sm font-semibold shadow-md transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-2"
                                                    title="Remove Video Entry"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                    Remove
                                                </button>
                                            </td>

                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="mt-12 p-8 text-center text-gray-500 text-lg italic bg-white border border-orange-100 rounded-xl shadow-sm">
                        No video entries to display yet. Let's create one!
                    </div>
                )}
                {/* --- Edit Modal --- */}
                {editModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center">
                        <div className="bg-white rounded-lg p-6 w-full max-w-2xl shadow-lg">
                            <h2 className="text-xl font-bold text-[#ea764d] mb-4">Edit Video Entry</h2>
                            <label className="block mb-2 text-sm font-medium text-gray-700">Heading</label>
                            <input
                                type="text"
                                value={editVideoData?.heading || ''}
                                onChange={(e) => setEditVideoData({ ...editVideoData, heading: e.target.value })}
                                className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
                            />

                            <label className="block mb-2 text-sm font-medium text-gray-700">Description</label>
                            <textarea
                                value={editVideoData?.description || ''}
                                onChange={(e) => setEditVideoData({ ...editVideoData, description: e.target.value })}
                                className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
                            />

                            {/* --- Video Upload Section for Edit Modal --- */}
                            <div className="mb-4">
                                <label
                                    htmlFor="editVideoInput" // Changed ID
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
                                >
                                    <svg
                                        className="mr-2 h-5 w-5 text-[#ea764d]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    <span>Upload New Video(s)</span> {/* Changed text */}
                                </label>
                                <input
                                    type="file"
                                    id="editVideoInput" // Changed ID
                                    accept="video/*" // Changed accept attribute
                                    onChange={(e) => handleVideoUpload(e, true)} // Changed handler
                                    multiple
                                    className="hidden"
                                />
                            </div>

                            {editUploadProgress > 0 && (
                                <div className="mt-2">
                                    <div className="bg-gray-200 rounded-full h-2.5 mb-4 dark:bg-gray-700">
                                        <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${editUploadProgress}%` }}></div>
                                    </div>
                                    <p className="text-sm text-gray-600 text-center">{Math.round(editUploadProgress)}%</p>
                                </div>
                            )}

                            <div className="flex flex-wrap gap-3 mb-4">
                                {editUploadedVideoUrls?.map((url, idx) => ( // Iterating over video URLs
                                    <div key={idx} className="relative w-[100px] h-[80px] border rounded overflow-hidden">
                                        <video
                                            src={url}
                                            controls
                                            className="w-full h-full object-cover"
                                            preload="metadata"
                                        />
                                        <button
                                            onClick={() => handleDeleteVideoFromSelection(idx, true)} // Changed handler
                                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                                            title="Remove video" // Changed title
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>
                            {/* --- End Video Upload Section for Edit Modal --- */}


                            <div className="flex justify-end gap-4">
                                <button
                                    onClick={() => setEditModalOpen(false)}
                                    className="bg-gray-300 px-4 py-2 rounded hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpdateVideo}
                                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                                >
                                    {isLoading ? <Loader /> : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* You might need to define these keyframe animations in your global CSS or a dedicated CSS file if you haven't already */}
                <style jsx>{`
                    @keyframes fade-in-down {
                      from {
                        opacity: 0;
                        transform: translateY(-20px);
                      }
                      to {
                        opacity: 1;
                        transform: translateY(0);
                      }
                    }

                    @keyframes slide-in-top {
                      from {
                        opacity: 0;
                        transform: translateY(-10px);
                      }
                      to {
                        opacity: 1;
                        transform: translateY(0);
                      }
                    }

                    @keyframes fade-in {
                      from {
                        opacity: 0;
                      }
                      to {
                        opacity: 1;
                      }
                    }

                    .animate-fade-in-down {
                      animation: fade-in-down 0.8s ease-out forwards;
                    }

                    .animate-slide-in-top {
                      animation: slide-in-top 0.6s ease-out forwards;
                    }

                    .animate-fade-in {
                      animation: fade-in 0.5s ease-out forwards;
                    }
                `}</style>
            </div >
        </div>

    );
};

export default Videogallery;